/**
 * Text-to-Speech Utility for AI Therapist Voice Responses
 * Primary: OpenAI TTS API (realistic voices)
 * Fallback: Web Speech API (browser voices)
 */

class TextToSpeechService {
  constructor() {
    // Web Speech API (fallback)
    this.synth = window.speechSynthesis;
    this.currentUtterance = null;
    this.voices = [];
    this.voicesLoaded = false;
    
    // Audio element for playing backend-generated audio
    this.audioElement = null;
    this.currentAudioCallback = null;
    
    // Track which method is being used
    this.usingBackendTTS = true;
    
    // Load browser voices when they become available
    this.loadVoices();
    
    // Some browsers require voice loading on voiceschanged event
    if (this.synth) {
      this.synth.onvoiceschanged = () => {
        this.loadVoices();
      };
    }
  }

  loadVoices() {
    if (this.synth) {
      this.voices = this.synth.getVoices();
      this.voicesLoaded = this.voices.length > 0;
    }
  }

  // Get available voices
  getVoices() {
    if (!this.voicesLoaded) {
      this.loadVoices();
    }
    return this.voices;
  }

  // Find best matching voice for therapist
  findVoice(voiceConfig) {
    const voices = this.getVoices();
    
    if (!voices || voices.length === 0) {
      console.warn('No voices available');
      return null;
    }

    // Try to find exact match by name
    if (voiceConfig.name) {
      const exactMatch = voices.find(v => v.name === voiceConfig.name);
      if (exactMatch) return exactMatch;
    }

    // Try to find by language and gender preference
    const languageMatches = voices.filter(v => 
      v.lang.startsWith(voiceConfig.language || 'en')
    );

    if (languageMatches.length === 0) {
      return voices[0]; // Fallback to first available voice
    }

    // Filter by gender if specified
    if (voiceConfig.gender) {
      const genderMatch = languageMatches.find(v => {
        const nameLower = v.name.toLowerCase();
        if (voiceConfig.gender === 'female') {
          return nameLower.includes('female') || 
                 nameLower.includes('woman') || 
                 nameLower.includes('samantha') ||
                 nameLower.includes('victoria') ||
                 nameLower.includes('karen') ||
                 nameLower.includes('zira') ||
                 nameLower.includes('susan');
        } else if (voiceConfig.gender === 'male') {
          return nameLower.includes('male') || 
                 nameLower.includes('man') || 
                 nameLower.includes('daniel') ||
                 nameLower.includes('david') ||
                 nameLower.includes('mark') ||
                 nameLower.includes('alex') && !nameLower.includes('alexa');
        }
        return false;
      });
      
      if (genderMatch) return genderMatch;
    }

    // Return first language match
    return languageMatches[0];
  }

  // Speak text using backend OpenAI TTS (primary method)
  async speakWithBackend(text, therapistId, callbacks = {}) {
    try {
      console.log('🎙️ Generating realistic voice with OpenAI TTS...');
      
      // Stop any ongoing speech
      this.stop();

      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      callbacks.onStart?.();

      // Call backend TTS API
      const response = await fetch('/api/tts/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          text: text.substring(0, 4096), // Limit to OpenAI's max
          therapistId,
          speed: 1.0
        })
      });

      if (!response.ok) {
        throw new Error('TTS API request failed');
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'TTS generation failed');
      }

      console.log(`✨ ${data.cached ? 'Using cached' : 'Generated new'} realistic voice`);

      // Create audio element if needed
      if (!this.audioElement) {
        this.audioElement = new Audio();
      }

      // Convert base64 to blob URL
      const audioBlob = this.base64ToBlob(data.audio, 'audio/mpeg');
      const audioUrl = URL.createObjectURL(audioBlob);

      // Set up audio element
      this.audioElement.src = audioUrl;
      this.currentAudioCallback = callbacks;

      this.audioElement.onended = () => {
        URL.revokeObjectURL(audioUrl);
        callbacks.onEnd?.();
      };

      this.audioElement.onerror = () => {
        URL.revokeObjectURL(audioUrl);
        callbacks.onError?.('Audio playback error');
      };

      // Play audio
      await this.audioElement.play();
      
      return true;

    } catch (error) {
      console.warn('Backend TTS failed, falling back to browser TTS:', error);
      return false;
    }
  }

  // Speak text with voice configuration
  speak(text, voiceConfig = {}, callbacks = {}) {
    // Try backend TTS first (OpenAI - realistic voices)
    if (this.usingBackendTTS && voiceConfig.therapistId) {
      this.speakWithBackend(text, voiceConfig.therapistId, callbacks)
        .then(success => {
          if (!success) {
            // Fall back to browser TTS
            this.speakWithBrowser(text, voiceConfig, callbacks);
          }
        })
        .catch(() => {
          // Fall back to browser TTS
          this.speakWithBrowser(text, voiceConfig, callbacks);
        });
      return;
    }

    // Use browser TTS directly
    this.speakWithBrowser(text, voiceConfig, callbacks);
  }

  // Speak using browser's Web Speech API (fallback)
  speakWithBrowser(text, voiceConfig = {}, callbacks = {}) {
    if (!this.synth) {
      console.error('Speech synthesis not supported');
      callbacks.onError?.('Speech synthesis not supported in this browser');
      return null;
    }

    console.log('🔊 Using browser TTS (fallback)');

    // Stop any ongoing speech
    this.stop();

    // Create utterance
    const utterance = new SpeechSynthesisUtterance(text);
    
    // Find and set voice
    const voice = this.findVoice(voiceConfig);
    if (voice) {
      utterance.voice = voice;
    }

    // Set voice parameters
    utterance.rate = voiceConfig.rate || 1.0;
    utterance.pitch = voiceConfig.pitch || 1.0;
    utterance.volume = voiceConfig.volume || 1.0;
    utterance.lang = voiceConfig.language || 'en-US';

    // Set callbacks
    utterance.onstart = () => {
      callbacks.onStart?.();
    };

    utterance.onend = () => {
      this.currentUtterance = null;
      callbacks.onEnd?.();
    };

    utterance.onerror = (event) => {
      this.currentUtterance = null;
      callbacks.onError?.(event.error);
    };

    utterance.onpause = () => {
      callbacks.onPause?.();
    };

    utterance.onresume = () => {
      callbacks.onResume?.();
    };

    // Store and speak
    this.currentUtterance = utterance;
    this.synth.speak(utterance);

    return utterance;
  }

  // Helper: Convert base64 to Blob
  base64ToBlob(base64, mimeType) {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }

  // Stop current speech
  stop() {
    // Stop browser TTS
    if (this.synth) {
      this.synth.cancel();
      this.currentUtterance = null;
    }
    
    // Stop backend audio
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.currentTime = 0;
      this.currentAudioCallback = null;
    }
  }

  // Pause current speech
  pause() {
    if (this.synth && this.synth.speaking) {
      this.synth.pause();
    }
  }

  // Resume paused speech
  resume() {
    if (this.synth && this.synth.paused) {
      this.synth.resume();
    }
  }

  // Check if currently speaking
  isSpeaking() {
    const browserSpeaking = this.synth && this.synth.speaking;
    const audioPlaying = this.audioElement && !this.audioElement.paused;
    return browserSpeaking || audioPlaying;
  }

  // Check if paused
  isPaused() {
    return this.synth && this.synth.paused;
  }

  // Check if browser supports speech synthesis
  isSupported() {
    return 'speechSynthesis' in window;
  }
}

// Therapist voice configurations
export const therapistVoices = {
  bob: {
    name: 'Google US English Male', // Fallback to browser defaults
    gender: 'male',
    language: 'en-US',
    rate: 0.95,
    pitch: 0.9,
    volume: 1.0,
    description: 'Calm, warm male voice'
  },
  sarah: {
    name: 'Google US English Female',
    gender: 'female',
    language: 'en-US',
    rate: 1.0,
    pitch: 1.1,
    volume: 1.0,
    description: 'Friendly, supportive female voice'
  },
  james: {
    name: 'Google UK English Male',
    gender: 'male',
    language: 'en-GB',
    rate: 0.9,
    pitch: 0.85,
    volume: 1.0,
    description: 'Professional, reassuring British voice'
  },
  maria: {
    name: 'Google español Female',
    gender: 'female',
    language: 'es-US',
    rate: 0.95,
    pitch: 1.15,
    volume: 1.0,
    description: 'Warm, empathetic Spanish voice'
  },
  alex: {
    name: 'Alex', // macOS voice
    gender: 'male',
    language: 'en-US',
    rate: 1.0,
    pitch: 1.0,
    volume: 1.0,
    description: 'Clear, neutral voice'
  },
  sophia: {
    name: 'Google US English Female',
    gender: 'female',
    language: 'en-US',
    rate: 0.95,
    pitch: 1.2,
    volume: 1.0,
    description: 'Gentle, compassionate voice'
  },
  michael: {
    name: 'Google US English Male',
    gender: 'male',
    language: 'en-US',
    rate: 1.05,
    pitch: 0.95,
    volume: 1.0,
    description: 'Energetic, encouraging voice'
  },
  lisa: {
    name: 'Google US English Female',
    gender: 'female',
    language: 'en-US',
    rate: 0.9,
    pitch: 1.0,
    volume: 1.0,
    description: 'Soothing, mindful voice'
  },
  david: {
    name: 'Google UK English Male',
    gender: 'male',
    language: 'en-GB',
    rate: 0.95,
    pitch: 0.9,
    volume: 1.0,
    description: 'Authoritative, knowledgeable voice'
  },
  emma: {
    name: 'Google US English Female',
    gender: 'female',
    language: 'en-US',
    rate: 1.0,
    pitch: 1.15,
    volume: 1.0,
    description: 'Uplifting, positive voice'
  }
};

// Get voice config for therapist (fallback to hardcoded values)
export const getTherapistVoice = (therapistId, therapistData = null) => {
  // If therapist data includes voice settings from backend, use those
  if (therapistData && therapistData.voice) {
    return {
      therapistId, // Include therapistId for backend TTS
      name: therapistData.voice.name || 'Google US English',
      gender: therapistData.voice.gender || 'neutral',
      language: therapistData.voice.language || 'en-US',
      rate: therapistData.voice.rate || 1.0,
      pitch: therapistData.voice.pitch || 1.0,
      volume: therapistData.voice.volume || 1.0,
      description: therapistData.voice.description || 'Natural voice'
    };
  }
  
  // Fallback to hardcoded voice configs (include therapistId)
  const voiceConfig = therapistVoices[therapistId] || therapistVoices.bob;
  return {
    ...voiceConfig,
    therapistId
  };
};

// Create singleton instance
const ttsService = new TextToSpeechService();

export default ttsService;

